"use client";

import { Component, lazy, Suspense, useCallback, useRef, type ErrorInfo, type ReactNode } from "react";
import { motion } from "framer-motion";

import type { ThemeMode } from "@/store/theme-store";
import { GlobeDataPaths } from "@/src/login/globe-data-paths";
import type { GlobeRotation } from "@/src/login/globe-projection";
import type { TacticalGlobeProps } from "@/src/login/vendor/tactical-globe-types";

const TacticalGlobe3D = lazy(() => import("@/src/login/vendor/TacticalGlobe3D.js"));

const LOGGER_MARKERS = [
  { label: "DL-MUM", description: "Mumbai field logger • Live", latitude: 19.076, longitude: 72.8777, color: "#0ea5e9" },
  { label: "DL-DEL", description: "Delhi edge node • Active", latitude: 28.6139, longitude: 77.209, color: "#10b981" },
  { label: "DL-BLR", description: "Bengaluru ingest • Sync", latitude: 12.9716, longitude: 77.5946, color: "#6366f1" },
  { label: "DL-HYD", description: "Hyderabad relay • Online", latitude: 17.385, longitude: 78.4867, color: "#8b5cf6" },
  { label: "DL-SIN", description: "Singapore hub • Route", latitude: 1.3521, longitude: 103.8198, color: "#f59e0b" },
  { label: "DL-FRA", description: "Frankfurt archive • Standby", latitude: 50.1109, longitude: 8.6821, color: "#ec4899" },
  { label: "DL-SFO", description: "San Francisco cloud • Active", latitude: 37.7749, longitude: -122.4194, color: "#22d3ee" },
] satisfies TacticalGlobeProps["markers"];

const THEME_PROPS: Record<ThemeMode, TacticalGlobeProps> = {
  light: {
    markers: LOGGER_MARKERS,
    countries: [],
    mapStyle: {
      oceanColor: "#dbeafe",
      landFill: "#cbd5e1",
      landStroke: "#94a3b8",
      strokeWidth: 0.45,
      hoverColor: "#64748b",
      disabledColor: "#e2e8f0",
    },
    tooltip: {
      show: true,
      background: "rgba(255, 255, 255, 0.94)",
      textColor: "#0f172a",
      borderColor: "rgba(14, 165, 233, 0.28)",
    },
    grid: { show: true, color: "#64748b", opacity: 0.14 },
    layout: { cornerRadius: 0, padding: 8, showBorder: false },
    interaction: {
      autoRotate: true,
      autoRotateSpeed: 5,
      rotateX: 0,
      rotateY: 12,
      rotateZ: 0,
      enableDrag: true,
      dragSensitivity: 0.35,
      glowColor: "#0ea5e9",
      glowIntensity: 0.45,
      showStars: false,
      showLabels: false,
    },
  },
  dark: {
    markers: LOGGER_MARKERS,
    countries: [],
    mapStyle: {
      oceanColor: "#0b1220",
      landFill: "#1e293b",
      landStroke: "#475569",
      strokeWidth: 0.45,
      hoverColor: "#64748b",
      disabledColor: "#0f172a",
    },
    tooltip: {
      show: true,
      background: "rgba(15, 23, 42, 0.92)",
      textColor: "#e2e8f0",
      borderColor: "rgba(34, 211, 238, 0.28)",
    },
    grid: { show: true, color: "#475569", opacity: 0.2 },
    layout: { cornerRadius: 0, padding: 8, showBorder: false },
    interaction: {
      autoRotate: true,
      autoRotateSpeed: 5,
      rotateX: 0,
      rotateY: 12,
      rotateZ: 0,
      enableDrag: true,
      dragSensitivity: 0.35,
      glowColor: "#22d3ee",
      glowIntensity: 0.55,
      showStars: true,
      showLabels: false,
    },
  },
};

const COPY: Record<ThemeMode, { label: string; headline: string; accent: string; subtext: string; bg: string }> = {
  light: {
    bg: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
    label: "text-sky-700/85",
    headline: "text-slate-900",
    accent: "from-sky-600 via-blue-600 to-indigo-600",
    subtext: "text-slate-600",
  },
  dark: {
    bg: "linear-gradient(180deg, #020617 0%, #0f172a 100%)",
    label: "text-cyan-400/85",
    headline: "text-white",
    accent: "from-cyan-300 via-sky-200 to-indigo-300",
    subtext: "text-slate-400",
  },
};

type DataloggerLoginSceneProps = {
  mode: ThemeMode;
  compact?: boolean;
};

function GlobeFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted">
      Loading globe…
    </div>
  );
}

class GlobeErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("TacticalGlobe3D failed to render", error, info);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted">
          <p>Globe visualization unavailable.</p>
          <p className="text-xs opacity-70">Refresh the page or check the browser console for details.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export function DataloggerLoginScene({ mode, compact = false }: DataloggerLoginSceneProps) {
  const copy = COPY[mode];
  const globeProps = THEME_PROPS[mode];
  const globeRotationRef = useRef<GlobeRotation>({ lambda: 0, phi: 12, gamma: 0 });
  const handleGlobeRotation = useCallback((rotation: GlobeRotation) => {
    globeRotationRef.current = rotation;
  }, []);

  return (
    <div className="relative h-full w-full overflow-hidden" style={{ background: copy.bg }}>
      <div className="absolute inset-0">
        <GlobeErrorBoundary>
          <Suspense fallback={<GlobeFallback />}>
            <TacticalGlobe3D {...globeProps} onRotationChange={handleGlobeRotation} />
          </Suspense>
        </GlobeErrorBoundary>
      </div>

      <GlobeDataPaths mode={mode} markers={LOGGER_MARKERS} rotationRef={globeRotationRef} maxPaths={compact ? 6 : 12} />

      {!compact ? (
        <motion.div
          className="pointer-events-none absolute bottom-8 left-8 right-8 z-10 max-w-xl xl:bottom-10 xl:left-10"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.65 }}
        >
          <p className={`font-mono text-[0.62rem] uppercase tracking-[0.24em] xl:text-[0.65rem] ${copy.label}`}>
            Atech · Global telemetry mesh
          </p>
          <h2 className={`mt-2 text-2xl font-semibold tracking-tight xl:mt-3 xl:text-[1.85rem] ${copy.headline}`}>
            Field data loggers,
            <span className={`block bg-gradient-to-r bg-clip-text text-transparent ${copy.accent}`}>
              connected across the planet
            </span>
          </h2>
          <p className={`mt-2 max-w-md text-sm leading-relaxed xl:mt-3 ${copy.subtext}`}>
            Drag the globe to explore. Telemetry paths spark between logger nodes, then fade as new streams appear.
          </p>
        </motion.div>
      ) : null}
    </div>
  );
}
